import { BaseElement } from 'base';
import { bus } from 'bus';
import { sectionFragment } from 'section-fragment';
import { SLIDE_CART_TOPIC } from 'slide-cart-topic';
import { SEARCH_TOPIC } from 'search-topic';

const CONTENT_TOPIC = 'product:variant';
const MOBILE_MENU_TOPIC = 'mobile-menu:active';
const FOOTER_TOPIC = 'footer:in-view';
const BREADCRUMBS_TOPIC = 'product-breadcrumbs:in-view';
const INSET_TOPIC = 'viewport:bottom-inset';
const INSET_PROPERTY = '--viewport-bottom-inset';
const SECTION_TOPIC = 'sticky-cta:section';
const DEFAULT_OVERLAYS = [SLIDE_CART_TOPIC, MOBILE_MENU_TOPIC, SEARCH_TOPIC];
const BUTTON_TAG = 'action-button';
const HEADER_SLOT = ':scope > .sticky-cta__header';
const BUTTON_SLOT = ':scope > .sticky-cta__button';
const BARS = {
  header: HEADER_SLOT,
  light: `${BUTTON_SLOT} > .sticky-cta__bar--light`,
  dark: `${BUTTON_SLOT} > .sticky-cta__bar--dark`
};
const SLOTS = [HEADER_SLOT, BUTTON_SLOT];
const SOLID_PARTS = {
  [HEADER_SLOT]: `${HEADER_SLOT} > .sticky-header`,
  [BUTTON_SLOT]: `${BUTTON_SLOT} .sticky-button__action`
};

const SUBPIXEL_TOLERANCE = 0.01;

const coverage = (slot, solid) => {
  const box = solid.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return 0;
  return Math.ceil(
    slot.getBoundingClientRect().bottom - box.top - SUBPIXEL_TOLERANCE
  );
};

class StickyCta extends BaseElement {
  #action = null;
  #mainCtaOut = false;
  #breadcrumbsInView = false;
  #openOverlays = new Set();
  #inset = 0;
  #overlayFocus = null;
  #activeBar = null;
  #resize = null;

  onConnect() {
    this.#mainCtaOut = false;
    this.#breadcrumbsInView = false;
    this.#openOverlays = new Set();
    this.#inset = 0;
    this.#overlayFocus = null;
    this.#activeBar = null;
    this.listen(this, 'focusout', this.#onFocusOut);

    const mainCtaTopic = this.dataset.mainCtaTopic;
    if (mainCtaTopic) {
      this.subscribe(mainCtaTopic, this.#onMainCta);
    } else {
      console.error(
        '[sticky-cta] data-main-cta-topic is missing, the bar stays hidden:',
        this.id
      );
    }

    this.#overlayTopics().forEach(topic =>
      this.subscribe(topic, value => this.#onOverlay(topic, value))
    );
    this.subscribe(FOOTER_TOPIC, this.#onFooter);
    this.subscribe(BREADCRUMBS_TOPIC, this.#onBreadcrumbs);

    this.#resize = new ResizeObserver(() => this.#publishInset());
    this.cleanup(() => this.#resize.disconnect());
    this.#observeSolids();
    this.cleanup(() => this.#releaseInset());

    this.subscribeLive(this.dataset.topic || CONTENT_TOPIC, this.#onContent);
    bus.publish(SECTION_TOPIC, {
      productId: this.dataset.productId,
      sectionId: this.dataset.sectionId
    });
    this.cleanup(() => bus.publish(SECTION_TOPIC, null));

    this.#bindActions();
  }

  run(action) {
    this.#action = action;
    this.#bindActions();
    return this;
  }

  get #context() {
    return {
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
      availability: this.dataset.availability
    };
  }

  #overlayTopics() {
    const extra = (this.dataset.overlayTopics ?? '')
      .split(',')
      .map(topic => topic.trim())
      .filter(Boolean);
    return [...new Set([...DEFAULT_OVERLAYS, ...extra])];
  }

  async #bindActions() {
    if (!this.#action) return;
    await customElements.whenDefined(BUTTON_TAG);
    if (!this.isConnected) return;
    this.qa(BUTTON_TAG).forEach(button =>
      button.run(() => this.#activate(button))
    );
  }

  #activate(button) {
    const control = button.control;
    if (control && control !== document.activeElement)
      control.focus({ preventScroll: true, focusVisible: false });
    this.#activeBar = this.#barOf(button);
    try {
      return this.#action?.(this.#context);
    } finally {
      this.#activeBar = null;
    }
  }

  #onMainCta(inView) {
    this.#mainCtaOut = inView === false;
    this.#render();
  }

  #onOverlay(topic, value) {
    const wasOpen = this.#openOverlays.size > 0;
    if (value) {
      this.#openOverlays.add(topic);
    } else {
      this.#openOverlays.delete(topic);
    }
    const open = this.#openOverlays.size > 0;
    if (open && !wasOpen)
      this.#overlayFocus = this.#focusedBar() ?? this.#activeBar;
    this.#render();
    if (wasOpen && !open) this.#restoreOverlayFocus();
  }

  #onBreadcrumbs(inView) {
    this.#breadcrumbsInView = inView === true;
    this.#render();
  }

  #onFocusOut(event) {
    if (this.#openOverlays.size === 0 && !this.#breadcrumbsInView) return;
    if (this.contains(event.relatedTarget)) return;
    this.#render();
  }

  #restoreOverlayFocus() {
    const key = this.#overlayFocus;
    this.#overlayFocus = null;
    if (!key || this.hasAttribute('inert')) return;
    const active = document.activeElement;
    const lost =
      !active || active === document.body || Boolean(active.closest('[inert]'));
    if (lost) this.#focusBar(key);
  }

  #onFooter(inView) {
    const next = inView === true ? 'dark' : 'light';
    if (this.dataset.surface === next) return;
    const focused = this.#focusedBar();
    this.dataset.surface = next;
    if (focused === 'light' || focused === 'dark') this.#focusBar(next);
  }

  #onContent(state) {
    if (!state) return;
    const owner = this.dataset.productId;
    if (owner && String(state.productId) !== owner) return;
    const fresh = sectionFragment(
      state.sections,
      this.dataset.sectionId,
      this.id
    );
    if (!fresh) return;

    const focused = this.#focusedBar();
    SLOTS.forEach(selector => {
      const slot = this.q(selector);
      const next = fresh.querySelector(selector);
      if (slot && next) slot.replaceChildren(...next.childNodes);
    });
    if (fresh.dataset.variantId)
      this.dataset.variantId = fresh.dataset.variantId;
    if (fresh.dataset.availability)
      this.dataset.availability = fresh.dataset.availability;

    this.#observeSolids();
    this.#bindActions();
    if (focused) this.#focusBar(focused);
  }

  #render() {
    const overlaid = this.#openOverlays.size > 0;
    const active = document.activeElement;
    const focused = this.contains(active);
    const keyboard = focused && active.matches(':focus-visible');
    const visible = this.#mainCtaOut && (!this.#breadcrumbsInView || keyboard);
    this.dataset.state = visible ? 'visible' : 'hidden';
    this.toggleAttribute('inert', !visible || (overlaid && !focused));
    this.dataset.overlay = overlaid ? 'true' : 'false';
  }

  #focusedBar() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return null;
    return this.#barOf(active);
  }

  #barOf(element) {
    return (
      Object.keys(BARS).find(key => this.q(BARS[key])?.contains(element)) ??
      null
    );
  }

  #focusBar(key) {
    this.q(`${BARS[key]} ${BUTTON_TAG}`)?.control?.focus({
      preventScroll: true
    });
  }

  #solidParts() {
    return SLOTS.flatMap(selector => {
      const slot = this.q(selector);
      if (!slot) return [];
      return this.qa(SOLID_PARTS[selector]).map(solid => [slot, solid]);
    });
  }

  #observeSolids() {
    this.#resize.disconnect();
    this.#solidParts().forEach(([, solid]) => this.#resize.observe(solid));
  }

  #publishInset() {
    const next = this.dataset.mainCtaTopic
      ? Math.max(
          0,
          ...this.#solidParts().map(([slot, solid]) => coverage(slot, solid))
        )
      : 0;
    if (next === this.#inset) return;
    this.#inset = next;
    document.documentElement.style.setProperty(INSET_PROPERTY, `${next}px`);
    bus.publish(INSET_TOPIC, next);
  }

  #releaseInset() {
    document.documentElement.style.removeProperty(INSET_PROPERTY);
    if (this.#inset === 0) return;
    this.#inset = 0;
    bus.publish(INSET_TOPIC, 0);
  }
}

if (!customElements.get('sticky-cta'))
  customElements.define('sticky-cta', StickyCta);
